import React, { useEffect, useMemo, useState } from "react";
import {
  getSuperAdminCategories,
  PaginatedResponse,
  SuperAdminCategory,
} from "../../services/superAdminApi";

const cardCls = "bg-white rounded-xl shadow-sm border border-gray-200 p-4";

const formatDate = (date?: string) => {
  if (!date) return "-";
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const statusBadge = (status?: string) => {
  const active = (status || "").toLowerCase() === "active";
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold ${
        active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
      }`}
    >
      {active ? "Active" : "Inactive"}
    </span>
  );
};

const SuperAdminCategories: React.FC = () => {
  const [rows, setRows] = useState<SuperAdminCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [perPage, setPerPage] = useState(15);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState<PaginatedResponse<SuperAdminCategory> | null>(null);

  const fetchCategories = async (targetPage = page, searchOverride?: string, statusOverride?: string) => {
    const effectiveSearch = searchOverride ?? search;
    const effectiveStatus = statusOverride ?? status;

    try {
      setLoading(true);
      setError("");
      const res = await getSuperAdminCategories({
        per_page: perPage,
        page: targetPage,
        search: effectiveSearch || undefined,
        status: effectiveStatus || undefined,
      });
      setRows(res.data || []);
      setPagination(res);
      setPage(res.current_page || targetPage);
    } catch (e: any) {
      setError(e?.response?.data?.message || "Failed to load categories.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perPage]);

  const total = pagination?.total ?? rows.length;
  const currentPage = pagination?.current_page ?? 1;
  const lastPage = pagination?.last_page ?? 1;
  const activeCount = useMemo(
    () => rows.filter((row) => (row.status || "").toLowerCase() === "active").length,
    [rows]
  );
  const inactiveCount = useMemo(
    () => rows.filter((row) => (row.status || "").toLowerCase() === "inactive").length,
    [rows]
  );
  const totalServicesInPage = useMemo(
    () => rows.reduce((sum, row) => sum + Number(row.services_count || 0), 0),
    [rows]
  );

  return (
    <div className="space-y-5 p-1">
      <div>
        <h1 className="text-2xl font-semibold text-[#402B69]">Categories</h1>
        <p className="text-sm text-gray-500">
          Platform categories overview with status and linked services visibility.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <div className={cardCls}>
          <p className="text-xs text-gray-500">Total Categories</p>
          <p className="text-2xl font-bold text-[#402B69]">{total}</p>
        </div>
        <div className={cardCls}>
          <p className="text-xs text-gray-500">Active (Current Page)</p>
          <p className="text-2xl font-bold text-green-600">{activeCount}</p>
        </div>
        <div className={cardCls}>
          <p className="text-xs text-gray-500">Inactive (Current Page)</p>
          <p className="text-2xl font-bold text-red-600">{inactiveCount}</p>
        </div>
        <div className={cardCls}>
          <p className="text-xs text-gray-500">Services (Current Page)</p>
          <p className="text-2xl font-bold text-[#402B69]">{totalServicesInPage}</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex flex-wrap gap-3 items-end">
        <div className="min-w-[220px]">
          <label className="block text-xs text-gray-500 mb-1">Search</label>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Category name..."
            className="w-full border rounded-lg px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Status</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm"
          >
            <option value="">All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Rows per page</label>
          <select
            value={perPage}
            onChange={(e) => setPerPage(Number(e.target.value))}
            className="border rounded-lg px-3 py-2 text-sm"
          >
            <option value={15}>15</option>
            <option value={30}>30</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
        <button
          onClick={() => fetchCategories(1)}
          className="bg-[#885ABB] hover:bg-[#7748A4] text-white px-4 py-2 rounded-lg text-sm font-semibold"
        >
          Apply
        </button>
        <button
          onClick={() => {
            setSearch("");
            setStatus("");
            fetchCategories(1, "", "");
          }}
          className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-semibold"
        >
          Reset
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-6 text-gray-600">Loading categories...</div>
        ) : error ? (
          <div className="p-6 text-red-600">{error}</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">#</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Category</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Color</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Services</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Status</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td className="px-4 py-6 text-gray-500" colSpan={6}>
                        No categories found for selected filters.
                      </td>
                    </tr>
                  ) : (
                    rows.map((row) => (
                      <tr key={row.id} className="border-b last:border-b-0 hover:bg-gray-50/50">
                        <td className="px-4 py-3 text-gray-700">{row.id}</td>
                        <td className="px-4 py-3 text-gray-700">
                          <div className="font-medium text-[#402B69]">{row.name}</div>
                        </td>
                        <td className="px-4 py-3 text-gray-700">
                          {row.color_code ? (
                            <div className="flex items-center gap-2">
                              <span
                                className="inline-block h-4 w-4 rounded-full border border-gray-300"
                                style={{ backgroundColor: row.color_code }}
                              />
                              <span className="text-xs">{row.color_code}</span>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-500">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-700">{row.services_count ?? 0}</td>
                        <td className="px-4 py-3 text-gray-700">{statusBadge(row.status)}</td>
                        <td className="px-4 py-3 text-gray-700">{formatDate(row.created_at)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="border-t px-4 py-3 flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs text-gray-500">
                Showing page {currentPage} of {lastPage} ({total} total categories)
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => fetchCategories(currentPage - 1)}
                  disabled={currentPage <= 1 || loading}
                  className="px-3 py-1.5 rounded-lg border text-sm disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => fetchCategories(currentPage + 1)}
                  disabled={currentPage >= lastPage || loading}
                  className="px-3 py-1.5 rounded-lg border text-sm disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SuperAdminCategories;

